import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  Layers,
  CheckCircle2,
  XCircle,
  Target,
  Check,
  ArrowRight,
  GitBranch,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Input,
  Field,
  FieldLabel,
  FieldError,
  Textarea,
  Drawer,
  Modal,
  Tooltip,
} from '../../../../components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import {
  Program,
  programFormSchema,
  type ProgramFormData,
  TierFeatureKey,
  getProgramChildrenByParent,
  getProgramRoots,
  isProgramForestValid,
  sortProgramsByProgression,
  getProgramPathwaySubtreeDeleteOrder,
} from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { programService } from '../../../../services/program';
import { usePrograms, useAgeGroups } from '@/hooks/useDataStore';
import { fetchAgeGroupsQuery } from '../../../../queries/ageGroups';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { FeatureGate } from '@/components/FeatureGate';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { AcademySportSelectField } from '@/components/sports/AcademySportSelectField';
import { SportFilterBadges } from '@/components/sport/SportFilterBadges';
import { useAcademySports } from '@/queries/academySports';
import { resolvePrimaryAcademySportId } from '@/utils/sportPresentation';
import { uploadProgramImage } from '@/services/storage';
import { ACADEMY_PAGE_TOUR_STEP_IDS, PROGRAM_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '../../../../store/auth';
import { useCoverImageUpload } from '@/hooks/useCoverImageUpload';
import { ProgramPathwayChoiceModal } from '@/components/programs/ProgramPathwayChoiceModal';
import { ProgramTemplateImportWizard } from '@/components/programs/ProgramTemplateImportWizard';
import type { ProgramTemplateProviderId } from '@court-sync/shared';

function ProgramCard({
  program,
  ageGroupLabel,
  predecessorName,
  onEdit,
  onDelete,
}: {
  program: Program;
  ageGroupLabel: string | null;
  predecessorName: string | null;
  onEdit: (p: Program) => void;
  onDelete: (p: Program) => void;
}) {
  const skillCount = program.targetSkills?.length ?? 0;
  return (
    <Card className="relative flex flex-col hover:shadow-md transition-shadow @container/program-card">
      <CardContent className="flex flex-col gap-2.5 @sm/program-card:gap-3 p-3.5 @sm/program-card:p-4 flex-1">
        <div className="flex items-start justify-between gap-1.5">
          <div className="flex-1 min-w-0">
            <h3
              className="text-sm @sm/program-card:text-base font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2"
              title={program.name}
            >
              {program.name}
            </h3>
            <div className="flex flex-wrap items-center gap-1 mt-1.5">
              {program.isInvitationOnly && (
                <span className="px-1.5 py-0.5 text-[10px] @sm/program-card:text-xs font-medium rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                  Invite only
                </span>
              )}
              {program.isActive ? (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] @sm/program-card:text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                  <CheckCircle2 className="w-2.5 h-2.5 @sm/program-card:w-3 @sm/program-card:h-3 shrink-0" />
                  Active
                </span>
              ) : (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] @sm/program-card:text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                  <XCircle className="w-2.5 h-2.5 @sm/program-card:w-3 @sm/program-card:h-3 shrink-0" />
                  Inactive
                </span>
              )}
            </div>
            {program.description && (
              <p className="text-xs @sm/program-card:text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1.5">
                {program.description}
              </p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 shrink-0 -mr-0.5"
                aria-label="Program actions"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(program)}>
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(program)}
                className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <ul className="flex flex-col gap-1 text-xs @sm/program-card:text-sm text-gray-600 dark:text-gray-400 border-t border-border/60 pt-2.5 mt-0.5">
          {program.skillLevel && program.skillLevel !== 'any' && (
            <li className="flex items-center gap-1.5 min-w-0">
              <Layers className="w-3 h-3 shrink-0 text-gray-400" aria-hidden />
              <span className="capitalize truncate">{program.skillLevel}</span>
            </li>
          )}
          {ageGroupLabel && (
            <li className="flex items-center gap-1.5 min-w-0">
              <Target className="w-3 h-3 shrink-0 text-gray-400" aria-hidden />
              <span className="truncate" title={ageGroupLabel}>
                {ageGroupLabel}
              </span>
            </li>
          )}
          {predecessorName && (
            <li className="flex items-start gap-1.5 min-w-0">
              <ArrowRight className="w-3 h-3 shrink-0 text-gray-400 mt-0.5" aria-hidden />
              <span className="min-w-0 leading-snug">
                <span className="text-gray-500 dark:text-gray-500">Follows </span>
                <span className="font-medium text-gray-800 dark:text-gray-200 line-clamp-2">
                  {predecessorName}
                </span>
              </span>
            </li>
          )}
          <li className="text-[11px] @sm/program-card:text-xs font-medium text-gray-500 dark:text-gray-500">
            {skillCount} target skill{skillCount === 1 ? '' : 's'}
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}

function ProgramCardWithSplitAction({
  program,
  ageGroupLabel,
  predecessorName,
  programNameById,
  onEdit,
  onDelete,
  onAddParallel,
}: {
  program: Program;
  ageGroupLabel: string | null;
  predecessorName: string | null;
  programNameById: Record<string, string>;
  onEdit: (p: Program) => void;
  onDelete: (p: Program) => void;
  onAddParallel: (sibling: Program) => void;
}) {
  const parallelFollowsLabel = program.previousProgramId
    ? programNameById[program.previousProgramId] ?? 'previous stage'
    : 'pathway start';

  return (
    <div className="flex w-full flex-col items-stretch gap-2">
      <div className="min-w-0 w-full">
        <ProgramCard
          program={program}
          ageGroupLabel={ageGroupLabel}
          predecessorName={predecessorName}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
      <Tooltip
        content={`Add parallel program at this level (also follows ${parallelFollowsLabel})`}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full h-8 gap-1.5 text-xs border-indigo-200/80 dark:border-indigo-800/80 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
          aria-label={`Add parallel program at same level as ${program.name}`}
          onClick={() => onAddParallel(program)}
        >
          <GitBranch className="w-3.5 h-3.5 shrink-0" aria-hidden />
          Parallel branch
        </Button>
      </Tooltip>
    </div>
  );
}

function PathwayConnector({ compact }: { compact?: boolean }) {
  return (
    <div className="flex flex-col items-center shrink-0 w-full" aria-hidden="true">
      <span
        className={cn(
          'block w-0.5 bg-indigo-300 dark:bg-indigo-600',
          compact ? 'h-3' : 'h-4 sm:h-5'
        )}
      />
      <span className="block w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-indigo-500 dark:bg-indigo-400 ring-4 ring-gray-50 dark:ring-gray-950" />
      <span
        className={cn(
          'block w-0.5 bg-indigo-300 dark:bg-indigo-600',
          compact ? 'h-3' : 'h-4 sm:h-5'
        )}
      />
    </div>
  );
}

function PathwayAddProgramPlaceholder({
  afterProgram,
  onAdd,
}: {
  afterProgram: Program;
  onAdd: (after: Program) => void;
}) {
  return (
    <>
      <PathwayConnector compact />
      <Card className="@container/program-card w-full border-2 border-dashed border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-950/20 shadow-none">
        <CardContent className="p-3.5 @sm/program-card:p-4 flex flex-col items-center text-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/50">
            <Plus className="h-4 w-4 text-indigo-600 dark:text-indigo-300" aria-hidden />
          </div>
          <div className="min-w-0 w-full space-y-0.5">
            <p className="text-xs @sm/program-card:text-sm font-medium text-gray-700 dark:text-gray-300">
              Next stage
            </p>
            <p
              className="text-[11px] @sm/program-card:text-xs text-gray-500 dark:text-gray-400 leading-snug line-clamp-2"
              title={`After ${afterProgram.name}`}
            >
              After{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">{afterProgram.name}</span>
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full @sm/program-card:w-auto gap-1.5 shrink-0 border-indigo-200 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/50"
            onClick={() => onAdd(afterProgram)}
          >
            <Plus className="w-4 h-4 shrink-0" aria-hidden />
            Add program
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

function ProgramSubtree({
  program,
  childrenByParentId,
  ageGroupLabelById,
  programNameById,
  onEdit,
  onDelete,
  onAddNextToPathway,
  onAddParallel,
}: {
  program: Program;
  childrenByParentId: Map<string, Program[]>;
  ageGroupLabelById: Record<string, string>;
  programNameById: Record<string, string>;
  onEdit: (p: Program) => void;
  onDelete: (p: Program) => void;
  onAddNextToPathway: (afterProgram: Program) => void;
  onAddParallel: (sibling: Program) => void;
}) {
  const children = childrenByParentId.get(program.id) ?? [];
  const n = children.length;
  return (
    <div className="flex flex-col items-center w-full">
      <ProgramCardWithSplitAction
        program={program}
        ageGroupLabel={program.ageGroupId ? ageGroupLabelById[program.ageGroupId] ?? null : null}
        predecessorName={
          program.previousProgramId ? programNameById[program.previousProgramId] ?? null : null
        }
        programNameById={programNameById}
        onEdit={onEdit}
        onDelete={onDelete}
        onAddParallel={onAddParallel}
      />
      {children.length > 0 ? (
        <>
          <PathwayConnector />
          {children.length === 1 ? (
            <ProgramSubtree
              program={children[0]}
              childrenByParentId={childrenByParentId}
              ageGroupLabelById={ageGroupLabelById}
              programNameById={programNameById}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddNextToPathway={onAddNextToPathway}
              onAddParallel={onAddParallel}
            />
          ) : (
            <div className="relative w-full flex flex-col items-stretch">
              <div
                className="pointer-events-none absolute top-0 z-0 hidden h-0.5 bg-indigo-300 dark:bg-indigo-600 lg:block"
                style={{
                  left: `calc((100% - ${(n - 1) * 1.5}rem) / ${2 * n})`,
                  width: `calc((100% - ${(n - 1) * 1.5}rem) * ${(n - 1) / n} + ${(n - 1) * 1.5}rem)`,
                }}
                aria-hidden
              />
              <div
                className={cn(
                  'relative z-10 w-full flex flex-col gap-8',
                  n === 2 && 'lg:grid lg:grid-cols-2 lg:gap-x-4 lg:gap-y-0',
                  n === 3 && 'lg:grid lg:grid-cols-3 lg:gap-x-4 lg:gap-y-0',
                  n >= 4 && 'lg:grid lg:grid-cols-4 lg:gap-x-3 lg:gap-y-0'
                )}
              >
                {children.map((child) => (
                  <div key={child.id} className="flex min-w-0 flex-col items-center w-full">
                    <span
                      className="mb-0 block h-4 sm:h-5 w-0.5 shrink-0 bg-indigo-300 dark:bg-indigo-600 lg:mb-0"
                      aria-hidden
                    />
                    <ProgramSubtree
                      program={child}
                      childrenByParentId={childrenByParentId}
                      ageGroupLabelById={ageGroupLabelById}
                      programNameById={programNameById}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onAddNextToPathway={onAddNextToPathway}
                      onAddParallel={onAddParallel}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <PathwayAddProgramPlaceholder afterProgram={program} onAdd={onAddNextToPathway} />
      )}
    </div>
  );
}

export default function ProgramManagement() {
  const { currentAcademy } = useCurrentAcademy();
  const { sports: academySports } = useAcademySports(currentAcademy?.id);
  const defaultAcademySportId = resolvePrimaryAcademySportId(academySports);
  const [programSportFilter, setProgramSportFilter] = useState('');
  const {
    hasAccess: hasProgramsAccess,
    hasAccessFromAcademyDb,
    hasAccessFromOwnerSubscription,
    tierResolved,
  } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const shouldFetchPrograms =
    !!currentAcademy?.id &&
    (hasAccessFromAcademyDb ||
      hasAccessFromOwnerSubscription ||
      (tierResolved && hasProgramsAccess));
  const {
    programs,
    isLoading: programsLoading,
    error: programsError,
    refresh: refreshPrograms,
  } = usePrograms(currentAcademy?.id, {
    includeInactive: true,
    enabled: shouldFetchPrograms,
  });
  const loading = programsLoading && programs.length === 0;
  const { user } = useAuthStore();
  const { startPageTour } = usePageTourActions();
  const programCreateTourScheduledRef = useRef(false);
  const cancelProgramCreateTourScheduleRef = useRef<(() => void) | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<Program | null>(null);
  const [deleting, setDeleting] = useState<Program | null>(null);
  const [deletingPathwayRoot, setDeletingPathwayRoot] = useState<Program | null>(null);
  const [deletingPathway, setDeletingPathway] = useState(false);
  /** Indices of target skill rows showing the inline name/description form (others show summary + edit/delete). */
  const [expandedSkillIndices, setExpandedSkillIndices] = useState<Set<number>>(() => new Set());
  const [pathwayChoiceOpen, setPathwayChoiceOpen] = useState(false);
  const [templateWizardOpen, setTemplateWizardOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<ProgramTemplateProviderId | null>(
    null
  );
  const [createMode, setCreateMode] = useState<'standalone' | 'next' | 'parallel'>('standalone');
  const [parallelSiblingName, setParallelSiblingName] = useState<string | null>(null);

  const openNewProgramFlow = () => setPathwayChoiceOpen(true);

  const handleSelectPredefinedTemplate = (templateId: ProgramTemplateProviderId) => {
    setPathwayChoiceOpen(false);
    setSelectedTemplateId(templateId);
    setTemplateWizardOpen(true);
  };

  const handleSelectCustomPathway = () => {
    setPathwayChoiceOpen(false);
    openCreate();
  };

  const handleBackToPathwayChoice = () => {
    setTemplateWizardOpen(false);
    setSelectedTemplateId(null);
    setPathwayChoiceOpen(true);
  };

  const needsAgeGroups = panelOpen || templateWizardOpen || pathwayChoiceOpen;
  const {
    ageGroups: fetchedAgeGroups,
    isLoading: ageGroupsLoading,
  } = useAgeGroups(currentAcademy?.id, { enabled: needsAgeGroups });

  const ageGroups = useMemo(
    () =>
      fetchedAgeGroups
        .filter((g) => g.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [fetchedAgeGroups]
  );

  const pathwayModalOpen = pathwayChoiceOpen || templateWizardOpen;
  const wasPathwayModalOpenRef = useRef(false);

  useEffect(() => {
    if (!currentAcademy?.id) return;
    const justOpened = pathwayModalOpen && !wasPathwayModalOpenRef.current;
    wasPathwayModalOpenRef.current = pathwayModalOpen;
    if (justOpened) {
      void fetchAgeGroupsQuery(currentAcademy.id, { force: true });
    }
  }, [currentAcademy?.id, pathwayModalOpen]);

  const ageGroupLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const g of ageGroups) {
      m[g.id] = g.label;
    }
    return m;
  }, [ageGroups]);

  const programNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of programs) {
      m[p.id] = p.name;
    }
    return m;
  }, [programs]);

  const filteredPrograms = useMemo(() => {
    if (!programSportFilter) return programs;
    return programs.filter((p) => p.academySportId === programSportFilter);
  }, [programs, programSportFilter]);

  const programForestValid = useMemo(() => isProgramForestValid(filteredPrograms), [filteredPrograms]);
  const programRoots = useMemo(() => getProgramRoots(filteredPrograms), [filteredPrograms]);
  const childrenByParentId = useMemo(() => getProgramChildrenByParent(filteredPrograms), [filteredPrograms]);
  const pathwayStageCountByRootId = useMemo(() => {
    const m = new Map<string, number>();
    for (const root of programRoots) {
      m.set(root.id, getProgramPathwaySubtreeDeleteOrder(root.id, filteredPrograms).length);
    }
    return m;
  }, [programRoots, filteredPrograms]);

  const deletingPathwayStageCount = deletingPathwayRoot
    ? pathwayStageCountByRootId.get(deletingPathwayRoot.id) ?? 0
    : 0;
  const programsCycleFallbackOrder = useMemo(
    () => sortProgramsByProgression(filteredPrograms),
    [filteredPrograms]
  );

  const previousProgramCandidates = useMemo(() => {
    const list = editing ? programs.filter((p) => p.id !== editing.id) : [...programs];
    return list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [programs, editing]);

  useEffect(() => {
    if (programsError) {
      toast.error(programsError);
    }
  }, [programsError]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProgramFormData>({
    resolver: zodResolver(programFormSchema),
    defaultValues: {
      name: '',
      description: '',
      skillLevel: 'any',
      ageGroupId: null,
      previousProgramId: null,
      academySportId: undefined,
      targetSkills: [],
      goals: '',
      isActive: true,
      isInvitationOnly: false,
      imageUrl: undefined,
    },
  });

  const watchedImageUrl = watch('imageUrl') || '';

  const {
    displayUrl: coverDisplayUrl,
    uploading: uploadingCover,
    handleFileSelect: handleCoverFile,
    handleRemove: handleCoverRemove,
    clear: clearCoverUi,
    resolveImagePayloadForSubmit: resolveCoverImagePayload,
  } = useCoverImageUpload({
    academyId: currentAcademy?.id,
    entityId: editing?.id,
    imageUrl: watchedImageUrl,
    setImageUrl: (url) => setValue('imageUrl', url, { shouldDirty: true }),
    upload: uploadProgramImage,
  });

  const closePanel = useCallback(() => {
    clearCoverUi();
    setCreateMode('standalone');
    setParallelSiblingName(null);
    setPanelOpen(false);
    setEditing(null);
  }, [clearCoverUi]);

  usePageTourReady(
    'academy-programs-create',
    panelOpen && !editing && Boolean(currentAcademy)
  );

  useEffect(() => {
    if (!panelOpen) {
      programCreateTourScheduledRef.current = false;
      cancelProgramCreateTourScheduleRef.current?.();
      cancelProgramCreateTourScheduleRef.current = null;
    }
  }, [panelOpen]);

  const handleProgramCreateDrawerPanelOpen = useCallback(() => {
    if (
      editing ||
      programCreateTourScheduledRef.current ||
      isPageTourComplete(user?.preferences, 'academy-programs-create')
    ) {
      return;
    }
    programCreateTourScheduledRef.current = true;
    cancelProgramCreateTourScheduleRef.current?.();
    cancelProgramCreateTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'academy-programs-create',
      user?.preferences
    );
  }, [editing, startPageTour, user?.preferences]);

  const { fields, append, remove } = useFieldArray({ control, name: 'targetSkills' });

  const watchedTargetSkills = useWatch({ control, name: 'targetSkills' });

  const openCreate = (options?: { nextAfter?: Program; parallelTo?: Program }) => {
    clearCoverUi();
    setEditing(null);
    setExpandedSkillIndices(new Set());
    let previousProgramId: string | null = null;
    if (options?.parallelTo) {
      previousProgramId = options.parallelTo.previousProgramId ?? null;
      setCreateMode('parallel');
      setParallelSiblingName(options.parallelTo.name);
    } else if (options?.nextAfter) {
      previousProgramId = options.nextAfter.id;
      setCreateMode('next');
      setParallelSiblingName(null);
    } else {
      setCreateMode('standalone');
      setParallelSiblingName(null);
    }
    reset({
      name: '',
      description: '',
      skillLevel: 'any',
      ageGroupId: null,
      previousProgramId,
      academySportId: defaultAcademySportId,
      targetSkills: [],
      goals: '',
      isActive: true,
      isInvitationOnly: false,
      imageUrl: undefined,
    });
    setPanelOpen(true);
  };

  const openAddNextInPathway = (afterProgram: Program) => {
    openCreate({ nextAfter: afterProgram });
  };

  const openAddParallelBranch = (siblingProgram: Program) => {
    openCreate({ parallelTo: siblingProgram });
  };

  const openEdit = (p: Program) => {
    clearCoverUi();
    setEditing(p);
    setExpandedSkillIndices(new Set());
    reset({
      name: p.name,
      description: p.description ?? '',
      skillLevel: p.skillLevel ?? 'any',
      ageGroupId: p.ageGroupId ?? null,
      previousProgramId: p.previousProgramId ?? null,
      academySportId: p.academySportId ?? defaultAcademySportId,
      targetSkills: p.targetSkills?.length ? p.targetSkills : [],
      goals: p.goals ?? '',
      isActive: p.isActive,
      isInvitationOnly: p.isInvitationOnly ?? false,
      imageUrl: p.imageUrl ?? '',
    });
    setPanelOpen(true);
  };

  const addSkillRow = () => {
    const newIndex = fields.length;
    append({ name: '', description: '' });
    setExpandedSkillIndices((prev) => new Set(prev).add(newIndex));
  };

  const removeSkillRow = (index: number) => {
    remove(index);
    setExpandedSkillIndices((prev) => {
      const next = new Set<number>();
      for (const j of prev) {
        if (j === index) continue;
        if (j > index) next.add(j - 1);
        else next.add(j);
      }
      return next;
    });
  };

  const confirmSkillRow = (index: number) => {
    const name = getValues(`targetSkills.${index}.name`)?.trim();
    if (!name) {
      toast.error('Enter a skill name');
      return;
    }
    setExpandedSkillIndices((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const editSkillRow = (index: number) => {
    setExpandedSkillIndices((prev) => new Set(prev).add(index));
  };

  const onSubmit = async (data: ProgramFormData) => {
    if (!currentAcademy) return;
    const skills = (data.targetSkills ?? []).filter((s) => s.name?.trim());
    const ageGroupId =
      data.ageGroupId && String(data.ageGroupId).length > 0 ? String(data.ageGroupId) : undefined;
    const previousProgramId =
      data.previousProgramId && String(data.previousProgramId).length > 0
        ? String(data.previousProgramId)
        : null;
    const payload = { ...data, targetSkills: skills, ageGroupId: ageGroupId ?? null, previousProgramId };
    try {
      const imagePatch = resolveCoverImagePayload();
      if (editing) {
        await programService.updateProgram(editing.id, currentAcademy.id, { ...payload, ...imagePatch });
        toast.success('Program updated');
      } else {
        const { imageUrl: _img, ...createPayload } = payload;
        await programService.createProgram(currentAcademy.id, {
          ...createPayload,
          ...imagePatch,
        });
        toast.success('Program created');
      }
      closePanel();
      await refreshPrograms();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const confirmDelete = async () => {
    if (!currentAcademy || !deleting) return;
    try {
      await programService.deleteProgram(deleting.id, currentAcademy.id);
      toast.success('Program deleted');
      setDeleting(null);
      await refreshPrograms();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const confirmDeletePathway = async () => {
    if (!currentAcademy || !deletingPathwayRoot) return;
    setDeletingPathway(true);
    try {
      const { deletedCount } = await programService.deleteProgramPathway(
        deletingPathwayRoot.id,
        currentAcademy.id
      );
      toast.success(
        deletedCount === 1
          ? 'Pathway deleted'
          : `Pathway deleted (${deletedCount} programs removed)`
      );
      setDeletingPathwayRoot(null);
      await refreshPrograms();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete pathway');
    } finally {
      setDeletingPathway(false);
    }
  };

  if (!currentAcademy) {
    return null;
  }

  return (
    <div className="contents">
      <PageHeader
        title="Programs"
        help={
          <PageFeatureHelp title="Programs">
            <p>
              Player pathways, skill goals, and squads by skill level — use programs as templates when
              creating lessons and camps.
            </p>
          </PageFeatureHelp>
        }
        helpAnchorId={ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_NEW}
        actions={
          hasProgramsAccess && !loading && programs.length > 0 ? (
            <Button
              id={ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_NEW}
              type="button"
              size="sm"
              className="w-full md:w-auto"
              onClick={openNewProgramFlow}
            >
              <Plus aria-hidden />
              New program
            </Button>
          ) : undefined
        }
      />

      <FeatureGate
        feature={TierFeatureKey.PROGRAMS}
        mock="programs"
        description="Development programs structure player pathways with target skills and goals. When you create term lessons, camps, or private lessons, selecting a program prefills sport, skill level, age group, description, and cover image."
      >
        {academySports.filter((s) => s.isActive).length > 1 ? (
          <SportFilterBadges
            sports={academySports.filter((s) => s.isActive)}
            value={programSportFilter}
            onChange={setProgramSportFilter}
            className="mb-4"
          />
        ) : null}
        {loading ? (
          <p className="text-center text-gray-500 py-12">Loading programs…</p>
        ) : filteredPrograms.length === 0 ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={Layers} />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {programs.length === 0 ? 'No programs yet' : 'No programs for this sport'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {programs.length === 0
                ? 'Use a reference pathway (Australian, US, European, or UK style) or create your own programs to define skills, goals, and squads for distinct skill levels. Then select a program when creating term lessons, camps, or private lessons to prefill lesson details automatically.'
                : 'Try another sport filter or create a program for this sport.'}
            </p>
            {programs.length === 0 ? (
              <Button
                id={ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_NEW}
                type="button"
                onClick={openNewProgramFlow}
                className="gap-2"
              >
                <Plus className="w-4 h-4" /> New program
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => setProgramSportFilter('')}>
                Show all sports
              </Button>
            )}
          </DashboardEmptyStateCard>
        ) : !programForestValid ? (
          <ol
            id={ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_LIST}
            className="flex flex-col items-stretch m-0 p-0 list-none w-full max-w-2xl mx-auto"
          >
            {programsCycleFallbackOrder.map((p, indexInChain) => (
              <li key={p.id} className="flex flex-col items-center w-full">
                {indexInChain > 0 && (
                  <div className="flex flex-col items-center shrink-0 w-full" aria-hidden="true">
                    <span className="block w-0.5 h-5 bg-indigo-300 dark:bg-indigo-600" />
                    <span className="block w-2.5 h-2.5 rounded-full bg-indigo-500 dark:bg-indigo-400 ring-4 ring-gray-50 dark:ring-gray-950" />
                    <span className="block w-0.5 h-5 bg-indigo-300 dark:bg-indigo-600" />
                  </div>
                )}
                <ProgramCardWithSplitAction
                  program={p}
                  ageGroupLabel={p.ageGroupId ? ageGroupLabelById[p.ageGroupId] ?? null : null}
                  predecessorName={
                    p.previousProgramId ? programNameById[p.previousProgramId] ?? null : null
                  }
                  programNameById={programNameById}
                  onEdit={openEdit}
                  onDelete={setDeleting}
                  onAddParallel={openAddParallelBranch}
                />
              </li>
            ))}
          </ol>
        ) : (
          <div
            id={ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_LIST}
            className={cn(
              'grid w-full gap-10',
              programRoots.length === 1
                ? 'max-w-2xl mx-auto'
                : programRoots.length === 2
                  ? 'grid-cols-1 md:grid-cols-2'
                  : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
            )}
          >
            {programRoots.map((root, chainIndex) => (
              <section
                key={root.id}
                aria-labelledby={`pathway-heading-${root.id}`}
                className="@container/pathway flex flex-col items-stretch w-full min-w-0"
              >
                <header
                  id={`pathway-heading-${root.id}`}
                  className="mb-4 px-1 sm:px-2 space-y-3 @sm/pathway:text-center"
                >
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                      {programRoots.length > 1 ? `Pathway ${chainIndex + 1}` : 'Pathway'}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      <span className="line-clamp-2">
                        Starts at{' '}
                        <span className="font-medium text-gray-700 dark:text-gray-300">{root.name}</span>
                      </span>
                      {(pathwayStageCountByRootId.get(root.id) ?? 0) > 0 && (
                        <>
                          <span className="hidden sm:inline text-gray-400 dark:text-gray-500"> · </span>
                          <span className="block sm:inline text-gray-400 dark:text-gray-500 mt-0.5 sm:mt-0">
                            {(pathwayStageCountByRootId.get(root.id) ?? 0)} program
                            {(pathwayStageCountByRootId.get(root.id) ?? 0) === 1 ? '' : 's'}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full @sm/pathway:w-auto @sm/pathway:mx-auto gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:border-red-900/50 dark:hover:bg-red-950/40"
                    onClick={() => setDeletingPathwayRoot(root)}
                  >
                    <Trash2 className="w-3.5 h-3.5 shrink-0" aria-hidden />
                    Delete pathway
                  </Button>
                </header>
                <ProgramSubtree
                  program={root}
                  childrenByParentId={childrenByParentId}
                  ageGroupLabelById={ageGroupLabelById}
                  programNameById={programNameById}
                  onEdit={openEdit}
                  onDelete={setDeleting}
                  onAddNextToPathway={openAddNextInPathway}
                  onAddParallel={openAddParallelBranch}
                />
              </section>
            ))}
          </div>
        )}
      </FeatureGate>

      <Drawer
        isOpen={panelOpen}
        onClose={closePanel}
        panelId={!editing ? PROGRAM_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
        onPanelOpen={!editing ? handleProgramCreateDrawerPanelOpen : undefined}
        title={
          editing
            ? 'Edit program'
            : createMode === 'parallel'
              ? 'New parallel program'
              : createMode === 'next'
                ? 'New program in pathway'
                : 'New program'
        }
        width="lg"
        headerActions={
          !editing ? (
            <DrawerTourHelp
              tourKey="academy-programs-create"
              suggestion="New to programs? Tour this form before you save."
              buttonLabel="Tour this form"
            />
          ) : undefined
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closePanel}>
              Cancel
            </Button>
            <Button
              id={!editing ? PROGRAM_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
              type="submit"
              form="program-form"
              disabled={isSubmitting || uploadingCover}
              isLoading={isSubmitting || uploadingCover}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </div>
        }
      >
        <form id="program-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div id={!editing ? PROGRAM_CREATE_TOUR_STEP_IDS.DETAILS : undefined} className="space-y-4">
              {!editing && createMode === 'parallel' && parallelSiblingName && (
                <p className="text-sm text-indigo-800 dark:text-indigo-200 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 px-3 py-2">
                  Parallel branch alongside <strong>{parallelSiblingName}</strong> — uses the same
                  &quot;follows&quot; stage. Enable <strong>Invitation only</strong> if this split
                  is selective.
                </p>
              )}
              <Field data-invalid={!!errors.name}>
                <FieldLabel htmlFor="prog-name">Name *</FieldLabel>
                <Input id="prog-name" {...register('name')} aria-invalid={!!errors.name} />
                <FieldError>{errors.name?.message}</FieldError>
              </Field>
              <AcademySportSelectField
                academyId={currentAcademy?.id}
                control={control}
                name="academySportId"
                required
                description="Programs are scoped to one sport at your academy."
              />
              <Field>
                <FieldLabel htmlFor="prog-desc">Description</FieldLabel>
                <Textarea id="prog-desc" rows={3} {...register('description')} />
              </Field>
              <LessonCoverImageField
                displayUrl={coverDisplayUrl}
                uploading={uploadingCover}
                disabled={isSubmitting || uploadingCover}
                inputId="program-cover-upload"
                onFileSelect={handleCoverFile}
                onRemove={handleCoverRemove}
              />
              <input type="hidden" {...register('imageUrl')} />
              </div>
              <Field>
                <FieldLabel htmlFor="prog-skill">Target skill level</FieldLabel>
                <select id="prog-skill" className="input w-full" {...register('skillLevel')}>
                  <option value="any">Any</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </Field>
              {ageGroups.length > 0 && (
                <Field>
                  <FieldLabel htmlFor="prog-ag">Age group</FieldLabel>
                  <select id="prog-ag" className="input w-full" {...register('ageGroupId')}>
                    <option value="">None</option>
                    {ageGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <div
                id={!editing ? PROGRAM_CREATE_TOUR_STEP_IDS.PATHWAY : undefined}
                className="space-y-4"
              >
              <Field>
                <FieldLabel htmlFor="prog-prev">This program follows</FieldLabel>
                <select id="prog-prev" className="input w-full" {...register('previousProgramId')}>
                  <option value="">None (start of pathway)</option>
                  {previousProgramCandidates.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Link pathways so the list orders from first stage to last.
                </p>
              </Field>
              <Field>
                <FieldLabel>Goals</FieldLabel>
                <Textarea rows={3} placeholder="What players should achieve in this pathway…" {...register('goals')} />
              </Field>
              </div>

              <div id={!editing ? PROGRAM_CREATE_TOUR_STEP_IDS.SKILLS : undefined}>
                <div className="flex items-center justify-between mb-2">
                  <FieldLabel>Target skills (coaches tick progress per student)</FieldLabel>
                  <Button type="button" variant="outline" size="sm" onClick={addSkillRow}>
                    <Plus className="w-3 h-3 mr-1" /> Add skill
                  </Button>
                </div>
                <div className="space-y-3">
                  {fields.map((field, index) => {
                    const expanded = expandedSkillIndices.has(index);
                    const row = watchedTargetSkills?.[index];
                    const skillInputClass =
                      'focus-visible:ring-0 focus-visible:border-gray-300 dark:focus-visible:border-gray-600';
                    return (
                      <div
                        key={field.id}
                        className="flex gap-2 items-start rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/60 dark:bg-gray-800/30"
                      >
                        {expanded ? (
                          <>
                            <div className="flex-1 space-y-2 min-w-0">
                              <Input
                                placeholder="Skill name"
                                className={skillInputClass}
                                {...register(`targetSkills.${index}.name` as const)}
                              />
                              <Input
                                placeholder="Description (optional)"
                                className={skillInputClass}
                                {...register(`targetSkills.${index}.description` as const)}
                              />
                            </div>
                            <button
                              type="button"
                              className="mt-0.5 p-1.5 rounded-md shrink-0 hover:bg-gray-200/80 dark:hover:bg-gray-700 text-green-600 dark:text-green-400"
                              aria-label="Save skill"
                              onClick={() => confirmSkillRow(index)}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <input type="hidden" {...register(`targetSkills.${index}.name` as const)} />
                            <input type="hidden" {...register(`targetSkills.${index}.description` as const)} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {row?.name?.trim() || 'Skill'}
                              </p>
                              {row?.description?.trim() ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {row.description}
                                </p>
                              ) : null}
                            </div>
                            <div className="flex shrink-0 gap-0.5 items-start pt-0.5">
                              <button
                                type="button"
                                className="p-1.5 rounded-md hover:bg-gray-200/80 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                                aria-label="Edit skill"
                                onClick={() => editSkillRow(index)}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded-md hover:bg-gray-200/80 dark:hover:bg-gray-700 text-red-600 dark:text-red-400"
                                aria-label="Remove skill"
                                onClick={() => removeSkillRow(index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('isActive')} />
                Active
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" {...register('isInvitationOnly')} className="mt-1" />
                <span>
                  <span className="font-medium">Invitation only</span>
                  <span className="block text-gray-500 dark:text-gray-400 text-xs mt-0.5">
                    Students submit an expression of interest and must be invited before they can book lessons in this
                    program. Existing program members can still book directly.
                  </span>
                </span>
              </label>

        </form>
      </Drawer>

      {currentAcademy && (
        <>
          <ProgramPathwayChoiceModal
            isOpen={pathwayChoiceOpen}
            onClose={() => setPathwayChoiceOpen(false)}
            onSelectPredefined={handleSelectPredefinedTemplate}
            onSelectCustom={handleSelectCustomPathway}
            academySports={academySports}
          />
          {selectedTemplateId && (
            <ProgramTemplateImportWizard
              isOpen={templateWizardOpen}
              onClose={() => {
                setTemplateWizardOpen(false);
                setSelectedTemplateId(null);
              }}
              providerId={selectedTemplateId}
              academyId={currentAcademy.id}
              ageGroups={ageGroups}
              ageGroupsLoading={ageGroupsLoading}
              onCreated={() => {
                void refreshPrograms().catch(() => {
                  // Error is surfaced via the programs store state; swallow to avoid an unhandled rejection.
                });
              }}
              onBackToChoice={handleBackToPathwayChoice}
            />
          )}
        </>
      )}

      <Modal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete program?"
        maxWidth="md"
        stackLevel="elevated"
      >
        <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
          Delete program &quot;{deleting?.name}&quot;? Lessons may still reference it until updated.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void confirmDelete()}>
            Delete
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!deletingPathwayRoot}
        onClose={() => !deletingPathway && setDeletingPathwayRoot(null)}
        title="Delete entire pathway?"
        maxWidth="md"
        stackLevel="elevated"
      >
        <div className="space-y-3 mb-4 text-sm text-gray-700 dark:text-gray-300">
          <p>
            This will permanently delete <strong>{deletingPathwayStageCount}</strong> program
            {deletingPathwayStageCount === 1 ? '' : 's'} in this pathway, starting from{' '}
            <strong>{deletingPathwayRoot?.name}</strong>
            {deletingPathwayStageCount > 1 ? ' and including all linked stages below it' : ''}.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
            <li>Student skill progress and completions for these programs will be removed.</li>
            <li>Linked lessons will no longer reference these programs.</li>
            <li>Parallel branches under this pathway are included in the deletion.</li>
          </ul>
          <p className="text-red-700 dark:text-red-300 font-medium">This action cannot be undone.</p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => setDeletingPathwayRoot(null)}
            disabled={deletingPathway}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmDeletePathway()}
            disabled={deletingPathway}
          >
            {deletingPathway ? 'Deleting…' : 'Delete pathway'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
